<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">Invoices</h5>

    <div class="filter q-mb-md bg-gray flex justify-between">
      <div class="container-fluid overflow-auto">
        <div class="row items-center q-col-gutter-md">
          <q-input v-model="search" label="Invoice / Company" class="col-4" />

          <q-input class="col-4" label="From" :value="fromComputed" @input="() => $refs.fromRef.hide()">
            <q-popup-proxy ref="fromRef" transition-show="scale" transition-hide="scale">
              <div>
                <q-date v-model="from" landscape @input="() => $refs.fromRef.hide()" />
              </div>
            </q-popup-proxy>

            <template #prepend>
              <q-icon name="event" color="primary" class="cursor-pointer" />
            </template>
          </q-input>

          <q-input class="col-4" label="To" :value="toComputed" @input="() => $refs.toRef.hide()">
            <q-popup-proxy ref="toRef" transition-show="scale" transition-hide="scale">
              <div>
                <q-date v-model="to" landscape @input="() => $refs.toRef.hide()" />
              </div>
            </q-popup-proxy>

            <template #prepend>
              <q-icon name="event" color="primary" class="cursor-pointer" />
            </template>
          </q-input>
        </div>

        <div class="row q-my-sm q-col-gutter-md">
          <div class="col-4 offset-2">
            <q-btn
              flat
              color="primary"
              class="full-width"
              icon="cached"
              label="reset"
              :disable="loading"
              @click="reset"
            />
          </div>

          <div class="col-4">
            <q-btn
              color="primary"
              class="full-width"
              icon="search"
              label="Search"
              :disable="loading"
              @click="searchInvoices"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="q-mb-md">
      <DataTable wrap-cells :columns="columns">
        <template v-if="loading">
          <tr v-for="n in 10" :key="n">
            <td v-for="m in columns.length" :key="m">
              <q-skeleton type="text" />
            </td>
          </tr>
        </template>

        <tr v-else-if="!invoices.length">
          <td colspan="8" class="text-center">There are no invoices.</td>
        </tr>

        <tr v-for="invoice in invoices" v-else :key="invoice.id">
          <!-- Invoice no. + link -->
          <td>
            <a :href="invoice.hosted_invoice_url" target="_blank" rel="noopener noreferrer">
              {{ invoice.number }}
            </a>
          </td>

          <!-- Transporter trading name -->
          <td class="text-uppercase">{{ invoice.customer_name }}</td>

          <!-- Transporter email -->
          <td>{{ invoice.customer_email }}</td>

          <!-- Date issued (From/To) -->
          <td>
            <p class="q-mb-sm">
              <strong>From:</strong>
              {{ (invoice.created * 1000) | formatDate }}
            </p>

            <p class="q-mb-none">
              <strong>To:</strong>
              {{ (invoice.period_end * 1000) | formatDate }}
            </p>
          </td>

          <!-- Subtotal -->
          <td class="text-center">{{ (invoice.subtotal / 100) | formatCurrency }}</td>

          <!-- GST Tax -->
          <td class="text-center">{{ invoice.tax / 100 || 0 | formatCurrency }}</td>

          <!-- Total -->
          <td class="text-center">{{ (invoice.total / 100) | formatCurrency }}</td>

          <!-- Status -->
          <td class="text-center">
            <span v-if="invoice.paid" class="text-positive text-weight-medium">Paid</span>
            <span v-else class="text-negative text-weight-medium">Unpaid</span>
          </td>
        </tr>
      </DataTable>
    </div>

    <div class="flex justify-center q-gutter-sm">
      <q-btn
        dense
        flat
        rounded
        color="primary"
        icon="chevron_left"
        :disable="!prev || loading"
        @click="sendRequest('before')"
      />

      <q-btn
        dense
        flat
        rounded
        color="primary"
        icon-right="chevron_right"
        :disable="!next || loading"
        @click="sendRequest('next')"
      />
    </div>
  </Wrapper>
</template>

<script>
/* eslint-disable camelcase */
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import API from '@/apis';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTableNew.vue';
import { getErrorMessage } from '@/utils';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import { DATE_FORMAT } from '@/utils/constants';

export default {
  name: 'AdminInvoices',

  components: {
    Wrapper,
    DataTable,
  },

  mixins: [formatDateMixin],

  data() {
    return {
      loading: true,
      from: '',
      to: '',
      search: '',
      invoices: [],
      next: false,
      prev: false,
    };
  },

  computed: {
    columns() {
      return [
        'Invoice No.',
        'Transporter Company',
        'Transporter Email',
        'Date Issued',
        'Subtotal',
        'GST',
        'Total',
        'Status',
      ];
    },
    fromComputed() {
      return this.from ? format(this.from, DATE_FORMAT) : '';
    },

    toComputed() {
      return this.to ? format(this.to, DATE_FORMAT) : '';
    },
  },

  created() {
    this.sendRequest();
  },

  methods: {
    getParams(direction) {
      const params = {};

      if (this.search) {
        // See if currently searching for an invoice or a company
        const invoice = this.invoices.find(({ number }) => number === this.search.trim());

        if (invoice) {
          params.search = invoice.id;
        } else {
          params.search = this.search;
        }
      }

      if (this.from) {
        const fromValue = parse(this.from).valueOf() / 1000;

        params.from = Math.round(fromValue);
      }

      if (this.to) {
        const toValue = parse(this.to).valueOf() / 1000;

        params.to = Math.round(toValue);
      }

      if (direction === 'next') {
        params.after = this.invoices[this.invoices.length - 1].id;
      } else if (direction === 'before') {
        params.before = this.invoices[0].id;
      }

      return params;
    },
    resetFilters() {
      this.search = '';
      this.from = null;
      this.to = null;
      this.status = 'all';
      this.sendRequest();
    },
    async sendRequest(direction = '') {
      // INFO: This API will request stripe data, which only has 1 way pagination
      try {
        this.loading = true;

        const {
          data: { data, has_more },
        } = await this.$axios.request(API.admin.getInvoices(this.getParams(direction)));

        this.invoices = data;

        switch (direction) {
          case 'next':
            this.next = has_more;
            this.prev = true;
            break;
          case 'before':
            this.prev = has_more;
            /**
             * If has more previous is true, check if array at pagination limit
             * that means it's in the middle of pagination (not first or last page)
             * If not then next value is opposite of previous
             */
            this.next = this.prev ? this.invoices.length === 10 : !this.prev;
            break;
          default:
            this.next = has_more;
            this.prev = false;
            break;
        }
      } catch (error) {
        this.$q.notify({
          message: getErrorMessage(error),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handler: this.sendRequest }],
        });
      } finally {
        this.loading = false;
      }
    },
    reset() {
      // Do nothing if at initial state
      if (this.search === undefined && this.from === undefined && this.to === undefined && this.currentPage === 1) {
        return;
      }

      this.currentPage = 1;
      this.totalPages = 1;
      this.search = undefined;
      this.from = undefined;
      this.to = undefined;
      this.sendRequest();
    },
    searchInvoices() {
      // Do nothing if no input
      if (!this.search && !this.from && !this.to) {
        return;
      }

      this.currentPage = 1;
      this.totalPages = 1;
      this.sendRequest();
    },
  },
};
</script>
